/**
 * PickupOptions - Child Web Component
 *
 * Handles local pickup store search and availability.
 * This component is a child of DeliveryOptions and focuses only on:
 * - Location search form handling
 * - Fetching store availability from Shopify Storefront API
 * - Rendering store results with dual availability (Click & Collect / In-store)
 * - Store selection with radio group behavior
 * - Stock filtering toggle
 * - Location caching in localStorage
 *
 * Communicates with parent via custom events:
 * - 'status-change': Emits status updates for row display
 * - 'store-selected': Emits when a store is confirmed for pickup
 */

import { StorefrontService } from './storefront-service.js';
import { CartService } from './cart-service.js';
import { Translator } from './translator.js';
import { GeocodingService } from './geocoding-service.js';

class PickupOptions extends HTMLElement {
  // Storage keys
  static STORAGE_LOCATION_KEY = 'amb.pickup_location';
  static STORAGE_SELECTED_STORE_KEY = 'amb.pickup_selected_store';
  static STORAGE_CONFIRMED_STORE_KEY = 'amb.pickup_confirmed_store';

  // Default in-store safety stock threshold (overridden by config)
  // If quantityAvailable > instore_safety_stock, show "In-store: Available"
  static DEFAULT_INSTORE_SAFETY_STOCK = 2;

  // Minimum number of store results to request and show in results-view
  static MIN_PICKUP_RESULTS = 10;

  // View states
  VIEW_FORM = 'form';
  VIEW_LOADING = 'loading';
  VIEW_RESULTS = 'results';
  VIEW_EMPTY = 'empty';

  // Data from parent
  /** @type {{ context?: string, storefront?: Object, cc_safety_stock?: number, instore_safety_stock?: number, [key: string]: any }} */
  data = {};

  // Translator instance
  translator = null;

  // Component state
  state = {
    view: 'form',
    loading: false,
    error: null,
    searchQuery: '',
    stores: [],
    filteredStores: [],
    filterByStock: false,
    selectedStoreId: null,    // Currently highlighted/selected in the list
    confirmedStoreId: null,   // Actually confirmed store (via Select Store button)
    confirmedStore: null,     // Full confirmed store object
    variantAvailabilities: [] // Raw availability data for individual variant lookups
  };

  // Cached data
  savedLocation = null;
  zipCodes = {};
  _lastAllLocations = [];

  // Storefront service instance
  storefrontService = null;

  // Geocoding service instance
  geocodingService = null;

  // User's geocoded location (from search query)
  userLocation = null;

  // DOM References
  formView = null;
  loadingView = null;
  resultsView = null;
  emptyView = null;
  form = null;
  searchInput = null;
  clearButton = null;
  stockFilter = null;
  resultsContainer = null;
  footerActions = null;
  selectStoreBtn = null;
  formMessages = null;

  // Event handlers for cleanup
  boundHandlers = {};

  // Initialization flag
  initialized = false;

  constructor() {
    super();
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.queryElements();
    this.registerEvents();
  }

  disconnectedCallback() {
    this.removeEvents();
    this.initialized = false;
  }

  // ============================================
  // INITIALIZATION (called by parent)
  // ============================================

  /**
   * Initialize component with data from parent
   * @param {Object} data - Configuration data
   */
  init(data) {
    this.data = data;

    // Initialize Translator (no namespace - access keys with full paths)
    this.translator = new Translator('delivery-translations');

    // Initialize StorefrontService with API credentials
    this.initStorefrontService();

    // Initialize GeocodingService with Google Maps API credentials
    this.initGeocodingService();

    this.loadSavedLocation();
    this.restoreSavedLocation();
    this.updateClearButtonVisibility();

    // Auto-fetch stores if we have a saved location
    this.autoFetchIfCached();
  }

  /**
   * Auto-fetch stores if there's a saved location
   * Similar to shipping-options behavior
   */
  autoFetchIfCached() {
    // If we have a confirmed store, emit its status and update cart line items
    if (this.state.confirmedStore) {
      const status = this.getStoreAvailabilityStatus(this.state.confirmedStore);
      const statusText = this.getStoreAvailabilityText(this.state.confirmedStore);
      const instoreStatus = this.getInstoreAvailabilityStatus(this.state.confirmedStore);
      const instoreText = this.getInstoreAvailabilityText(this.state.confirmedStore);
      this.emitStatusChange(status, this.state.confirmedStore.name, null, statusText, instoreStatus, instoreText);
      // Update cart line items with confirmed store availability
      this.updateCartLineItemsAvailability(this.state.confirmedStore);
    }

    if (this.savedLocation && this.storefrontService) {
      // Set search query from saved location
      if (this.searchInput) {
        this.searchInput.value = this.savedLocation;
      }
      // Auto-fetch stores (this will update availability)
      this.searchStores();
    } else if (!this.state.confirmedStore) {
      // Emit normal status only if no saved location and no confirmed store
      this.emitStatusChange('normal');
      // Hide cart line items if no confirmed store
      this.hideCartLineItemsAvailability();
    }
  }

  /**
   * Initialize the StorefrontService with Storefront API credentials
   */
  initStorefrontService() {
    const storefront = this.data.storefront;

    if (!storefront || !storefront.accessToken) {
      console.warn('PickupOptions: Storefront API access token not configured');
      return;
    }

    this.storefrontService = new StorefrontService({
      shopDomain: storefront.shopDomain,
      apiVersion: storefront.apiVersion,
      accessToken: storefront.accessToken,
      locationId: this.data.location_id
    });
  }

  /**
   * Initialize the GeocodingService for geocoding and distance calculation
   */
  initGeocodingService() {
    this.geocodingService = new GeocodingService({
      apiKey: this.data.googleMapsApiKey || '',
      defaultCountry: this.data.countryIsoCode || 'AU'
    });
  }

  /**
   * Handle variant change from parent
   * @param {Object} data - Variant data
   */
  handleVariantChange(data) {
    this.data = { ...this.data, ...data };

    // Clear availability cache when variant changes
    if (this.storefrontService) {
      this.storefrontService.clearCache();
    }

    // Re-fetch if we have a saved location - availability may differ for new variant
    if (this.savedLocation) {
      this.searchStores();
    } else {
      // Clear results if no saved location
      this.clearResults();
    }
  }

  /**
   * Handle cart change from parent
   * @param {Object} data - Cart data
   */
  handleCartChange(data) {
    this.data = data;

    // Re-query DOM elements (they may have been replaced)
    this.queryElements();

    // Re-initialize StorefrontService with new data
    this.initStorefrontService();

    // Re-initialize GeocodingService with new data (country may have changed)
    this.initGeocodingService();

    // Clear availability cache when cart changes
    if (this.storefrontService) {
      this.storefrontService.clearCache();
    }

    // Restore saved location to form (input may have been re-rendered)
    this.restoreSavedLocation();
    this.updateClearButtonVisibility();

    // Restore filter toggle visual state to match internal state
    this.restoreFilterToggleState();

    // Re-fetch if we have a saved location
    if (this.savedLocation) {
      this.searchStores();
    }
  }

  /**
   * Restore the filter toggle's visual state to match internal state
   * Called after DOM elements are re-queried (e.g., after cart update)
   */
  restoreFilterToggleState() {
    if (this.stockFilter) {
      this.stockFilter.setAttribute('aria-checked', String(this.state.filterByStock));
    }
  }

  /**
   * Handle drawer close from parent
   * Restores the confirmed store selection (discards any uncommitted changes)
   */
  handleDrawerClose() {
    // Restore selection to the confirmed store
    if (this.state.confirmedStoreId) {
      this.state.selectedStoreId = this.state.confirmedStoreId;
      // Update visual selection in the list
      this.updateStoreCardSelection(this.state.confirmedStoreId);
    }
  }

  /**
   * Update store card selection visuals
   * @param {string} storeId
   */
  updateStoreCardSelection(storeId) {
    const storeCards = this.resultsContainer?.querySelectorAll('[data-store-id]') || [];
    storeCards.forEach(card => {
      const isSelected = String(card.dataset.storeId) === String(storeId);
      const isDisabled = card.classList.contains('pickup-options__store-card--disabled');
      
      // Don't highlight disabled stores
      if (isDisabled) {
        card.setAttribute('aria-checked', 'false');
        card.classList.remove('pickup-options__store-card--selected');
      } else {
        card.setAttribute('aria-checked', String(isSelected));
        card.classList.toggle('pickup-options__store-card--selected', isSelected);
      }
    });
    this.updateSelectButton();
  }

  // ============================================
  // DOM QUERIES
  // ============================================

  /**
   * Query and store references to DOM elements
   */
  queryElements() {
    // Views
    this.formView = this.querySelector('[ref="form-view"]');
    this.loadingView = this.querySelector('[ref="loading-view"]');
    this.resultsView = this.querySelector('[ref="results-view"]');
    this.emptyView = this.querySelector('[ref="empty-view"]');

    // Form elements
    this.form = this.querySelector('[ref="form"]');
    this.searchInput = this.querySelector('[ref="search-input"]');
    this.clearButton = this.querySelector('[ref="clear-btn"]');
    this.stockFilter = this.querySelector('[ref="stock-filter"]');
    this.formMessages = this.querySelector('[ref="form-messages"]');

    // Results
    this.resultsContainer = this.querySelector('[ref="results-container"]');

    // Footer
    this.footerActions = this.querySelector('[ref="footer-actions"]');
    this.selectStoreBtn = this.querySelector('[ref="select-store-btn"]');
  }

  // ============================================
  // EVENT REGISTRATION
  // ============================================

  /**
   * Register all event listeners
   */
  registerEvents() {
    // Form submit
    if (this.form) {
      this.boundHandlers.submit = this.handleSubmit.bind(this);
      this.form.addEventListener('submit', this.boundHandlers.submit);
    }

    // Search input
    if (this.searchInput) {
      this.boundHandlers.input = this.handleInputChange.bind(this);
      this.searchInput.addEventListener('input', this.boundHandlers.input);
    }

    // Clear button
    if (this.clearButton) {
      this.boundHandlers.clear = this.handleClear.bind(this);
      this.clearButton.addEventListener('click', this.boundHandlers.clear);
    }

    // Stock filter toggle
    if (this.stockFilter) {
      this.boundHandlers.filterToggle = this.handleFilterToggle.bind(this);
      this.stockFilter.addEventListener('click', this.boundHandlers.filterToggle);
    }

    // Select store button
    if (this.selectStoreBtn) {
      this.boundHandlers.selectStore = this.handleSelectStore.bind(this);
      this.selectStoreBtn.addEventListener('click', this.boundHandlers.selectStore);
    }

    // Results container for store selection (event delegation)
    if (this.resultsContainer) {
      this.boundHandlers.storeClick = this.handleStoreClick.bind(this);
      this.boundHandlers.storeKeydown = this.handleStoreKeydown.bind(this);
      this.resultsContainer.addEventListener('click', this.boundHandlers.storeClick);
      this.resultsContainer.addEventListener('keydown', this.boundHandlers.storeKeydown);
    }
  }

  /**
   * Remove all event listeners
   */
  removeEvents() {
    if (this.form && this.boundHandlers.submit) {
      this.form.removeEventListener('submit', this.boundHandlers.submit);
    }
    if (this.searchInput && this.boundHandlers.input) {
      this.searchInput.removeEventListener('input', this.boundHandlers.input);
    }
    if (this.clearButton && this.boundHandlers.clear) {
      this.clearButton.removeEventListener('click', this.boundHandlers.clear);
    }
    if (this.stockFilter && this.boundHandlers.filterToggle) {
      this.stockFilter.removeEventListener('click', this.boundHandlers.filterToggle);
    }
    if (this.selectStoreBtn && this.boundHandlers.selectStore) {
      this.selectStoreBtn.removeEventListener('click', this.boundHandlers.selectStore);
    }
    if (this.resultsContainer) {
      if (this.boundHandlers.storeClick) {
        this.resultsContainer.removeEventListener('click', this.boundHandlers.storeClick);
      }
      if (this.boundHandlers.storeKeydown) {
        this.resultsContainer.removeEventListener('keydown', this.boundHandlers.storeKeydown);
      }
    }
    this.boundHandlers = {};
  }

  // ============================================
  // FORM HANDLERS
  // ============================================

  /**
   * Handle form submission
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();
    await this.searchStores();
  }

  /**
   * Handle input change - toggle clear button visibility
   * @param {InputEvent} event
   */
  handleInputChange(event) {
    this.updateClearButtonVisibility();
    this.hideFormMessages();
  }

  /**
   * Handle clear button click
   * @param {MouseEvent} event
   */
  handleClear(event) {
    event.preventDefault();
    if (this.searchInput) {
      this.searchInput.value = '';
      this.searchInput.focus();
    }
    this.updateClearButtonVisibility();
    this.clearResults();
    this.hideFormMessages();
  }

  /**
   * Update clear button visibility based on input value
   */
  updateClearButtonVisibility() {
    if (!this.clearButton || !this.searchInput) return;

    const hasValue = this.searchInput.value.trim().length > 0;
    this.clearButton.classList.toggle('hidden', !hasValue);
  }

  // ============================================
  // FILTER HANDLERS
  // ============================================

  /**
   * Handle stock filter toggle
   * @param {MouseEvent} event
   */
  handleFilterToggle(event) {
    event.preventDefault();
    this.state.filterByStock = !this.state.filterByStock;

    // Update toggle visual state
    if (this.stockFilter) {
      this.stockFilter.setAttribute('aria-checked', String(this.state.filterByStock));
    }

    // Apply filter to current results
    this.applyStockFilter();
  }

  /**
   * Apply stock filter to stores
   * Note: Selection state (selectedStoreId) is preserved - if the selected store
   * is filtered out, it will be re-highlighted when the filter is toggled off
   */
  applyStockFilter() {
    if (this.state.stores.length === 0) return;

    if (this.state.filterByStock) {
      this.state.filteredStores = this.state.stores.filter(store => this.isStoreAvailable(store));
    } else {
      this.state.filteredStores = [...this.state.stores];
    }

    // Re-render with filtered results
    if (this.state.filteredStores.length > 0) {
      this.renderStores(this.state.filteredStores);
      this.showView(this.VIEW_RESULTS);
    } else {
      this.showView(this.VIEW_EMPTY);
      this.hideFooter();
    }
  }

  /**
   * Check if store has FULL availability for all items
   * Used by the stock filter toggle - only shows stores that can fulfill the entire order
   * @param {Object} store - Store object
   * @returns {boolean} True if store has full availability (not partial)
   */
  isStoreAvailable(store) {
    // New consolidated format
    if (store.availability) {
      const status = store.availability.status;
      // Only 'available' status means the store can fulfill ALL items
      // 'partial' means some items are out of stock - don't show when filter is enabled
      return status === 'available';
    }

    // Legacy format support (for backwards compatibility)
    return store.available ||
           store.click_collect_available ||
           store.instore_available;
  }

  // ============================================
  // STORE SELECTION HANDLERS
  // ============================================

  /**
   * Handle store card click
   * @param {MouseEvent} event
   */
  handleStoreClick(event) {
    const storeCard = event.target.closest('[data-store-id]');
    if (storeCard) {
      // Don't allow selection if store is disabled
      if (storeCard.classList.contains('pickup-options__store-card--disabled')) {
        return;
      }
      const storeId = storeCard.dataset.storeId;
      this.selectStore(storeId);
    }
  }

  /**
   * Handle keyboard navigation in store list
   * @param {KeyboardEvent} event
   */
  handleStoreKeydown(event) {
    const storeCard = event.target.closest('[data-store-id]');
    if (!storeCard) return;

    // Don't allow selection if store is disabled
    const isDisabled = storeCard.classList.contains('pickup-options__store-card--disabled');

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!isDisabled) {
          this.selectStore(storeCard.dataset.storeId);
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.focusNextStore(storeCard);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusPreviousStore(storeCard);
        break;
    }
  }

  /**
   * Focus next store card (skip disabled stores)
   * @param {HTMLElement} current
   */
  focusNextStore(current) {
    let next = current.nextElementSibling;
    while (next) {
      if (next.dataset.storeId && !next.classList.contains('pickup-options__store-card--disabled')) {
        next.focus();
        return;
      }
      next = next.nextElementSibling;
    }
  }

  /**
   * Focus previous store card (skip disabled stores)
   * @param {HTMLElement} current
   */
  focusPreviousStore(current) {
    let prev = current.previousElementSibling;
    while (prev) {
      if (prev.dataset.storeId && !prev.classList.contains('pickup-options__store-card--disabled')) {
        prev.focus();
        return;
      }
      prev = prev.previousElementSibling;
    }
  }

  /**
   * Select a store (preview selection before confirming)
   * @param {string} storeId
   */
  selectStore(storeId) {
    // Don't allow selection of disabled stores
    const store = this.state.stores.find(s => String(s.id) === String(storeId));
    if (store && store.disabled === true) {
      return;
    }

    this.state.selectedStoreId = storeId;

    // Update visual selection in the list
    this.updateStoreCardSelection(storeId);

    // Save selection for when user re-opens the drawer
    this.saveSelectedStore(storeId);
  }

  /**
   * Update select button state
   */
  updateSelectButton() {
    if (!this.selectStoreBtn) return;
    this.selectStoreBtn.disabled = !this.state.selectedStoreId;
  }

  /**
   * Handle select store button click
   * @param {MouseEvent} event
   */
  async handleSelectStore(event) {
    event.preventDefault();
    if (!this.state.selectedStoreId) return;

    const selectedStore = this.state.stores.find(
      store => String(store.id) === String(this.state.selectedStoreId)
    );

    // Don't allow selection of disabled stores
    if (!selectedStore || selectedStore.disabled === true) {
      return;
    }

    if (selectedStore) {
      // Save as confirmed store (localStorage)
      this.saveConfirmedStore(selectedStore);

      // Get availability status and text for the selected store
      const availabilityStatus = this.getStoreAvailabilityStatus(selectedStore);
      const availabilityText = this.getStoreAvailabilityText(selectedStore);

      // Save to cart attributes (for checkout page)
      // await this.savePickupStoreToCart(selectedStore, availabilityStatus);

      await this.savePickupPreferredStoreToCart(selectedStore);

      // Update cart line items with pickup availability (cart context only)
      this.updateCartLineItemsAvailability(selectedStore);

      // Get in-store availability for PDP context
      const instoreStatus = this.getInstoreAvailabilityStatus(selectedStore);
      const instoreText = this.getInstoreAvailabilityText(selectedStore);

      // Emit store selected event to parent (triggers drawer close)
      this.dispatchEvent(new CustomEvent('store-selected', {
        bubbles: true,
        detail: {
          store: selectedStore,
          status: availabilityStatus,
          statusText: availabilityText,
          instoreStatus,
          instoreText
        }
      }));

      // Update status to show selected store with correct availability
      this.emitStatusChange(availabilityStatus, selectedStore.name, null, availabilityText, instoreStatus, instoreText);
    }
  }

  /**
   * Save pickup store selection to cart attributes
   * This persists the selection for checkout page
   * @param {Object} store - Selected store object
   * @param {string} availabilityStatus - Availability status
   */
  async savePickupStoreToCart(store, availabilityStatus) {
    try {
      await CartService.savePickupStore({
        searchQuery: this.state.searchQuery || this.savedLocation || '',
        location: {
          id: store.id,
          name: store.name,
          formattedAddress: store.address || store.addressFormatted || '',
          status: store.availability?.status || availabilityStatus || 'available'
        }
      });
    } catch (error) {
      // Don't block the selection if cart save fails
      console.warn('PickupOptions: Failed to save pickup store to cart:', error);
    }
  }

  /**
   * Save pickup store selection to cart attributes
   * This persists the selection for checkout page
   * @param {Object} store - Selected store object
   */
  async savePickupPreferredStoreToCart(store) {
    try {
      await CartService.savePickupPreferredStore({
        searchQuery: this.state.searchQuery || this.savedLocation || '',
        location: {
          id: store.id,
          name: store.name
        }
      });
    } catch (error) {
      // Don't block the selection if cart save fails
      console.warn('PickupOptions: Failed to save pickup store to cart:', error);
    }
  }

  /**
   * Get store availability status for row display
   * Returns the actual status ('available', 'partial', 'unavailable')
   * @param {Object} store
   * @returns {'available' | 'partial' | 'unavailable'}
   */
  getStoreAvailabilityStatus(store) {
    if (!store.availability) return 'available';
    return store.availability.status || 'available';
  }

  /**
   * Get store availability text for display
   * @param {Object} store
   * @returns {string}
   */
  getStoreAvailabilityText(store) {
    if (!store.availability) return this.t('delivery.pickup.store_available');
    return store.availability.statusText || this.t('delivery.pickup.store_available');
  }

  /**
   * Get in-store availability status for row display (PDP only)
   * @param {Object} store
   * @returns {'available' | 'partial' | 'unavailable'}
   */
  getInstoreAvailabilityStatus(store) {
    if (!store.instoreAvailability) return 'unavailable';
    return store.instoreAvailability.status || 'unavailable';
  }

  /**
   * Get in-store availability text for display (PDP only)
   * @param {Object} store
   * @returns {string}
   */
  getInstoreAvailabilityText(store) {
    if (!store.instoreAvailability) return this.t('delivery.pickup.instore_unavailable');
    return store.instoreAvailability.statusText || this.t('delivery.pickup.instore_unavailable');
  }

  /**
   * Update confirmed store availability from fresh results
   * @param {Array} stores - Fresh store results with updated availability
   */
  updateConfirmedStoreAvailability(stores) {
    if (!this.state.confirmedStoreId) return;

    // Find the confirmed store in the new results
    const confirmedInResults = stores.find(
      store => String(store.id) === String(this.state.confirmedStoreId)
    );

    if (confirmedInResults) {
      this.state.confirmedStore = confirmedInResults;
      try {
        localStorage.setItem(PickupOptions.STORAGE_CONFIRMED_STORE_KEY, JSON.stringify(confirmedInResults));
        this.saveSelectedStore(confirmedInResults.id);
      } catch (error) {
        console.error('PickupOptions: Error updating confirmed store availability', error);
      }
      this.updateCartLineItemsAvailability(confirmedInResults);
    }
  }

  /**
   * Update cart line items with pickup availability status
   * Similar logic to checkout-ui/src/checkout/cart-line-item.render-after.tsx
   *
   * @param {Object} store - Selected store with availability data
   */
  updateCartLineItemsAvailability(store) {
    if (this.data.context !== 'cart') return;

    const variantIds = this.data.shippableLineItems || [];
    const quantities = this.data.quantities || variantIds.map(() => 1);
    const locationId = store.id;
    const shippableLineItemsKey = this.data.shippableLineItemsKey || [];

    // Get availability details for this store from the storefrontService cache
    // If not available, use the aggregated status from the store object
    variantIds.forEach((variantId, index) => {
      const quantity = quantities[index] || 1;
      const lineItemKey = shippableLineItemsKey[index] || null;

      // Try to get individual variant availability from cached data
      const variantAvailability = this.getVariantAvailabilityAtStore(variantId, locationId, quantity, index);


      // Update the cart line item element
      this.updateCartLineItemElement(variantId, variantAvailability, lineItemKey);
    });
  }

  /**
   * Get availability status for a specific variant at a store
   * Matches checkout-ui/src/checkout/cart-line-item.render-after.tsx logic
   *
   * Status determination order:
   * 1. digital: !requiresShipping (non-physical products)
   * 2. not_applicable: storeAvailability === null (store doesn't carry variant)
   * 3. unavailable: quantityAvailable <= 0
   * 4. partially_available: quantityAvailable < quantity
   * 5. low_available: quantityAvailable - quantity < cc_safety_stock
   * 6. available: otherwise
   *
   * @param {string} variantId - Variant ID
   * @param {string} locationId - Store location ID
   * @param {number} quantity - Required quantity
   * @param {number} [lineIndex] - Line item index (cart context); use for lookup when same variant appears multiple times (e.g. one-time vs subscription)
   * @returns {{ status: string, text: string }}
   */
  getVariantAvailabilityAtStore(variantId, locationId, quantity, lineIndex) {
    const store = this.state.confirmedStore;
    const ccSafetyStock = this.data.cc_safety_stock ?? 10;

    // Helper to build context for translations
    const buildContext = (quantityAvailable = 0) => ({
      availability: {
        all: quantity,
        available: Math.min(quantityAvailable, quantity),
        unavailable: Math.max(0, quantity - quantityAvailable),
        quantityAvailable: quantityAvailable
      },
      store: store ? { id: store.id, name: store.name } : null
    });

    // Check if this is a digital item (doesn't require shipping)
    const digitalItems = this.data.digitalLineItems || [];
    const isDigital = digitalItems.some(id =>
      this.normalizeVariantId(id) === this.normalizeVariantId(variantId)
    );

    if (isDigital) {
      return {
        status: 'digital',
        text: this.t('delivery.pickup.line_item_digital', buildContext(quantity))
      };
    }

    if (!store) {
      return {
        status: 'unavailable',
        text: this.t('delivery.pickup.line_item_unavailable', buildContext(0))
      };
    }

    // Normalize location ID to full GID format for comparison
    const normalizedLocationId = this.storefrontService?.toLocationGid(locationId) || locationId;

    // Look up individual variant availability from cached data
    const variantAvailabilities = this.state.variantAvailabilities || [];

    // Find the availability array for this variant
    const variantData = variantAvailabilities.find((avArray, index) =>
      avArray && avArray.length > 0 && index === lineIndex &&
      this.normalizeVariantId(avArray[0]?.variant_id) === this.normalizeVariantId(variantId)
    );

    // Find availability at this specific location (only this location's inventory is used)
    const locationAvailability = variantData?.find(av =>
      av.location?.id === normalizedLocationId
    );

    // Not applicable: variant not found at this location
    if (!locationAvailability) {
      return {
        status: 'not_applicable',
        text: this.t('delivery.pickup.line_item_not_applicable', buildContext(0))
      };
    }

    // Use only this location's quantityAvailable for display (never aggregate or other location's stock)
    const quantityAvailable = locationAvailability.quantityAvailable ?? 0;
    const context = buildContext(quantityAvailable);

    // Available if BOTH conditions are met:
    // 1. Can fulfill the order: inventory >= quantity
    // 2. Inventory above safety stock: inventory > threshold
    const canFulfill = quantityAvailable >= quantity;
    const aboveThreshold = quantityAvailable > ccSafetyStock;

    if (canFulfill && aboveThreshold) {
      return {
        status: 'available',
        text: this.t('delivery.pickup.cc_available', context)
      };
    }

    return {
      status: 'unavailable',
      text: this.t('delivery.pickup.cc_unavailable', context)
    };
  }

  /**
   * Normalize variant ID to short numeric format for comparison
   * @param {string} variantId
   * @returns {string}
   */
  normalizeVariantId(variantId) {
    if (!variantId) return '';
    const str = String(variantId);
    // Extract numeric ID from GID format if present
    const match = str.match(/gid:\/\/shopify\/ProductVariant\/(\d+)/);
    return match ? match[1] : str;
  }


  /**
   * Update a single cart line item's pickup availability element
   * @param {string} variantId - Variant ID
   * @param {{ status: string, text: string }} availability - Availability info
   * @param {string} lineItemKey - Line item key
   */
  updateCartLineItemElement(variantId, availability, lineItemKey) {
    const elements = document.querySelectorAll(`.pickup-availability[data-line-item-key="${lineItemKey}"]`);

    elements.forEach(element => {
      // Show the element
      element.classList.remove('hidden');

      // Update indicator status
      const indicator = element.querySelector('.availability-indicator');
      if (indicator) {
        indicator.className = 'availability-indicator';
        indicator.classList.add(`availability-indicator--${availability.status}`);
      }

      // Update text
      const textElement = element.querySelector('.availability-text');
      if (textElement) {
        textElement.textContent = availability.text;
      }
    });
  }

  /**
   * Hide all cart line item pickup availability indicators
   */
  hideCartLineItemsAvailability() {
    if (this.data.context !== 'cart') return;

    const elements = document.querySelectorAll('.pickup-availability');
    elements.forEach(element => element.classList.add('hidden'));
  }

  /**
   * Emit status based on confirmed store or general availability
   * @param {Array} stores - Store results
   * @param {string} query - Search query
   */
  emitStatusBasedOnSelection(stores, query) {
    // If we have a confirmed store, use its status
    if (this.state.confirmedStore) {
      const status = this.getStoreAvailabilityStatus(this.state.confirmedStore);
      const statusText = this.getStoreAvailabilityText(this.state.confirmedStore);
      const instoreStatus = this.getInstoreAvailabilityStatus(this.state.confirmedStore);
      const instoreText = this.getInstoreAvailabilityText(this.state.confirmedStore);
      this.emitStatusChange(status, this.state.confirmedStore.name, null, statusText, instoreStatus, instoreText);
    } else {
      // No confirmed store - show general availability based on search
      const hasAvailable = stores.some(store => this.isStoreAvailable(store));
      this.emitStatusChange(
        hasAvailable ? 'available' : 'unavailable',
        query
      );
    }
  }

  // ============================================
  // LOCATION MANAGEMENT
  // ============================================

  /**
   * Load saved location and confirmed store from localStorage
   */
  loadSavedLocation() {
    try {
      this.savedLocation = localStorage.getItem(PickupOptions.STORAGE_LOCATION_KEY) || null;

      // Load selected store ID (for list highlighting)
      const savedStoreId = localStorage.getItem(PickupOptions.STORAGE_SELECTED_STORE_KEY);
      if (savedStoreId) {
        this.state.selectedStoreId = savedStoreId;
      }

      // Load confirmed store (the one user actually confirmed)
      const confirmedStoreData = localStorage.getItem(PickupOptions.STORAGE_CONFIRMED_STORE_KEY);
      if (confirmedStoreData) {
        try {
          const confirmedStore = JSON.parse(confirmedStoreData);
          this.state.confirmedStoreId = confirmedStore.id;
          this.state.confirmedStore = confirmedStore;
          // Also set selectedStoreId to match confirmed
          this.state.selectedStoreId = confirmedStore.id;
        } catch (e) {
          console.warn('PickupOptions: Failed to parse confirmed store data');
        }
      }
    } catch (error) {
      console.error('PickupOptions: Error loading saved data', error);
      this.savedLocation = null;
    }
  }

  /**
   * Restore saved location to form
   */
  restoreSavedLocation() {
    if (this.savedLocation && this.searchInput) {
      this.searchInput.value = this.savedLocation;
    }
  }

  /**
   * Save location to localStorage
   * @param {string} location
   */
  saveLocation(location) {
    this.savedLocation = location;
    try {
      localStorage.setItem(PickupOptions.STORAGE_LOCATION_KEY, location);
    } catch (error) {
      console.error('PickupOptions: Error saving location', error);
    }
  }

  /**
   * Clear saved location from localStorage (e.g., when stale/invalid query detected)
   */
  clearSavedLocation() {
    this.savedLocation = null;
    try {
      localStorage.removeItem(PickupOptions.STORAGE_LOCATION_KEY);
    } catch (error) {
      // silent
    }
  }

  /**
   * Save selected store ID to localStorage (for list highlighting)
   * @param {string} storeId
   */
  saveSelectedStore(storeId) {
    try {
      localStorage.setItem(PickupOptions.STORAGE_SELECTED_STORE_KEY, storeId);
    } catch (error) {
      console.error('PickupOptions: Error saving selected store', error);
    }
  }

  /**
   * Save confirmed store to localStorage (full store object)
   * @param {Object} store - Full store object
   */
  saveConfirmedStore(store) {
    this.state.confirmedStoreId = store.id;
    this.state.confirmedStore = store;
    try {
      localStorage.setItem(PickupOptions.STORAGE_CONFIRMED_STORE_KEY, JSON.stringify(store));
      this.saveSelectedStore(store.id);
      this.saveLocation(this.state.searchQuery);
    } catch (error) {
      console.error('PickupOptions: Error saving confirmed store', error);
    }
  }

  // ============================================
  // STORE SEARCH API
  // ============================================

  /**
   * Search for stores
   */
  async searchStores() {
    const query = this.searchInput?.value?.trim();

    if (!query) {
      this.showFormError(this.t('delivery.pickup.search_required'));
      return;
    }

    const isValidPostcode = await this.validatePostcode(query);
    if (!isValidPostcode) {
      this.showView(this.VIEW_FORM);
      this.showFormError(this.t('delivery.pickup.invalid_postcode'));
      this.emitStatusChange('unavailable');
      this.hideFooter();
      return;
    }

    this.state.searchQuery = query;

    // Preserve the current selection to restore if store is in results
    const previousSelectedId = this.state.selectedStoreId || this.state.confirmedStoreId;

    // Show loading state
    this.showView(this.VIEW_LOADING);
    this.emitStatusChange('loading');
    this.hideFormMessages();
    this.hideFooter();

    try {
      const stores = await this.fetchStores(query);

      if (this.isInvalidSearchQuery(query, stores, this._lastAllLocations)) {
        this.showView(this.VIEW_FORM);
        this.showFormError(this.t('delivery.pickup.invalid_location', {}, 'Please enter a valid postcode or suburb'));
        this.emitStatusChange('unavailable');
        this.hideFooter();
        this.clearSavedLocation();
        return;
      }

      if (stores.length > 0) {
        this.state.stores = stores;
        this.state.filteredStores = [...stores];

        // Restore selection if the previously selected store is in the new results
        if (previousSelectedId) {
          const storeInResults = stores.find(
            store => String(store.id) === String(previousSelectedId)
          );
          if (storeInResults) {
            this.state.selectedStoreId = previousSelectedId;
          } else {
            // Clear selection if store not in results
            this.state.selectedStoreId = null;
          }
        }

        // Apply filter if enabled
        if (this.state.filterByStock) {
          this.applyStockFilter();
        } else {
          this.renderStores(stores);
          this.showView(this.VIEW_RESULTS);
        }

        // Update confirmed store availability from new results
        this.updateConfirmedStoreAvailability(stores);

        // Emit status based on confirmed store or general availability
        this.emitStatusBasedOnSelection(stores, query);
      } else {
        this.state.stores = [];
        this.state.filteredStores = [];
        this.state.selectedStoreId = null;
        this.showView(this.VIEW_EMPTY);
        this.emitStatusChange('unavailable', query);
      }

    } catch (error) {
      console.error('PickupOptions: Error searching stores', error);
      this.showView(this.VIEW_FORM);
      this.showFormError(this.t('delivery.pickup.search_error'));
      this.emitStatusChange('unavailable', query, error.message);
    }
  }

  /**
   * Fetch stores from Shopify Storefront API
   * Uses StorefrontService for API communication and caching
   * Uses GeocodingService to geocode search query and calculate distances
   *
   * Search Strategy:
   * 1. Try to geocode the search query to get user's coordinates
   * 2. If geocode succeeds: Show all stores sorted by distance (nearest first)
   * 3. If geocode fails: Fall back to text-based filtering by name, city, zip, address
   *
   * @param {string} searchQuery - Location search query (postcode, suburb, etc.)
   * @returns {Promise<Array>} Array of store objects sorted by distance
   */
  async fetchStores(searchQuery) {
    if (!this.storefrontService) {
      throw new Error(this.t('delivery.pickup.api_not_configured'));
    }

    // Geocode the search query to get user's coordinates
    await this.geocodeSearchQuery(searchQuery);

    // Fetch locations from Storefront API (request at least MIN_PICKUP_RESULTS for results-view)
    const allLocations = await this.storefrontService.getAllLocations({
      limit: PickupOptions.MIN_PICKUP_RESULTS
    });
    this._lastAllLocations = allLocations;

    // Prefer locations that match the search query (postcode, city, etc.)
    const filteredLocations = this.filterLocationsByQuery(allLocations, searchQuery);
    let stores = filteredLocations.map(location => this.transformLocationToStore(location));

    // If geocoding succeeded, calculate distances and sort by nearest
    if (this.userLocation) {
      stores = this.calculateStoreDistances(stores);
      stores = this.sortStoresByDistance(stores);
    }

    // Pad to MIN_PICKUP_RESULTS only when query is legitimate
    const isNumericQuery = /^\d+$/.test(searchQuery);
    const knownNames = this.buildKnownLocationNames(allLocations);
    const isLegitimateQuery = isNumericQuery
      || filteredLocations.length > 0
      || this.hasValidGeocodePrecision()
      || this.matchesKnownLocation(searchQuery, knownNames);
    if (isLegitimateQuery && stores.length < PickupOptions.MIN_PICKUP_RESULTS && allLocations.length >= PickupOptions.MIN_PICKUP_RESULTS) {
      const allStores = allLocations.map(location => this.transformLocationToStore(location));
      const allSorted = this.userLocation
        ? this.sortStoresByDistance(this.calculateStoreDistances(allStores))
        : allStores;
      stores = allSorted.slice(0, PickupOptions.MIN_PICKUP_RESULTS);
    }

    // Fetch store availability for current product/cart items
    await this.enrichStoresWithAvailability(stores);

    return stores;
  }

  /**
   * Geocode the search query to get user's coordinates
   * @param {string} searchQuery - Location search query
   */
  async geocodeSearchQuery(searchQuery) {
    if (!this.geocodingService) {
      this.userLocation = null;
      return;
    }

    try {
      const geocodeResult = await this.geocodingService.geocode(searchQuery);

      if (geocodeResult) {
        this.userLocation = {
          lat: geocodeResult.lat,
          lng: geocodeResult.lng,
          formattedAddress: geocodeResult.formattedAddress,
          types: geocodeResult.types || []
        };
      } else {
        this.userLocation = null;
      }
    } catch (error) {
      console.warn('PickupOptions: Failed to geocode search query:', error);
      this.userLocation = null;
    }
  }

  static VALID_GEOCODE_TYPES = new Set([
    'street_address', 'route', 'intersection',
    'postal_code', 'postal_code_prefix',
    'locality', 'sublocality',
    'sublocality_level_1', 'neighborhood', 'colloquial_area',
    'administrative_area_level_2'
  ]);

  /**
   * Check if geocode result resolved to a precise-enough location
   * Rejects country-level or administrative-area-only results (e.g. "nowhere" → country:AU)
   * @returns {boolean}
   */
  hasValidGeocodePrecision() {
    if (!this.userLocation?.types?.length) return false;
    return this.userLocation.types.some(t => PickupOptions.VALID_GEOCODE_TYPES.has(t));
  }

  /**
   * Build a Set of known location names from store data and zipcode provinces.
   * Used as a fast fallback validation when geocoding is not configured.
   * @param {Array} allLocations - All locations from API
   * @returns {Set<string>}
   */
  buildKnownLocationNames(allLocations) {
    const known = new Set();
    for (const loc of allLocations) {
      const a = loc.address || {};
      if (a.city) known.add(a.city.toLowerCase());
      if (a.province) known.add(a.province.toLowerCase());
      if (a.provinceCode) known.add(a.provinceCode.toLowerCase());
    }
    // Add province names from zipcode data
    if (this.zipCodes) {
      for (const provinces of Object.values(this.zipCodes)) {
        for (const provinceName of Object.keys(provinces)) {
          known.add(provinceName.toLowerCase());
        }
      }
    }
    return known;
  }

  /**
   * Check if a non-numeric query matches any known location name
   * @param {string} query
   * @param {Set<string>} knownNames
   * @returns {boolean}
   */
  matchesKnownLocation(query, knownNames) {
    const q = query.toLowerCase().trim();
    if (q.length < 3) return false;
    for (const name of knownNames) {
      if (name.includes(q)) return true;
    }
    return false;
  }

  /**
   * Validate postcode when country zipcodes are available
   * @param {string} query
   * @returns {Promise<boolean>}
   */
  async validatePostcode(query) {
    if (!/^\d+$/.test(query)) {
      return true;
    }

    const zipcodeUrl = this.data.assets?.zipcodes;

    if (!zipcodeUrl) {
      return true;
    }

    const country = this.data.countryIsoCode;
    if (!this.zipCodes[country]) {
      try {
        const response = await fetch(zipcodeUrl);
        this.zipCodes[country] = await response.json();
      } catch (error) {
        console.error('PickupOptions: Error loading zipcodes', error);
        return true;
      }
    }

    const provinces = this.zipCodes[country];
    for (const [provinceName, postcodes] of Object.entries(provinces)) {
      if (postcodes.includes(query)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Calculate distances from user's location to each store
   * @param {Array} stores - Array of store objects
   * @returns {Array} Stores with calculated distances
   */
  calculateStoreDistances(stores) {
    if (!this.geocodingService || !this.userLocation) {
      // No geocoding available - return stores without distance
      return stores;
    }

    return this.geocodingService.calculateDistancesToLocations(
      this.userLocation,
      stores
    );
  }

  /**
   * Sort stores by distance (nearest first)
   * Stores without distance are placed at the end
   * @param {Array} stores - Array of store objects with distance
   * @returns {Array} Sorted stores
   */
  sortStoresByDistance(stores) {
    if (!this.geocodingService) {
      return stores;
    }

    return this.geocodingService.sortByDistance(stores);
  }

  /**
   * Enrich stores with product availability from Storefront API
   * @param {Array} stores - Array of store objects to enrich
   */
  async enrichStoresWithAvailability(stores) {
    if (stores.length === 0) return;

    try {
      // Get variant IDs and quantities based on context
      const { variantIds, quantities } = this.getVariantsForAvailability();

      if (variantIds.length === 0) {
        console.warn('PickupOptions: No variant IDs available for availability check');
        return;
      }

      // Fetch store availability for all variants
      const allAvailabilities = await this.storefrontService.getStoreAvailabilities(variantIds, quantities);

      const subscriptionLineItems = this.data.subscriptionLineItems || [];
      if (subscriptionLineItems.length > 0) {
        allAvailabilities.forEach((variantAvailabilities, index) => {
          const isSubscription = subscriptionLineItems[index] === 'true';
          if (isSubscription) {
            variantAvailabilities.forEach((av) => {
              av.quantityAvailable = 0;
              av.available = false;
            });
          }
        });
      }


      // Store raw availability data for individual variant lookups
      this.state.variantAvailabilities = allAvailabilities;

      // Update each store with availability data
      stores.forEach(store => {
        const locationId = store.id;

        // Get aggregated availability for this location
        const aggregated = this.storefrontService.getAggregatedAvailability(
          locationId,
          allAvailabilities,
          quantities
        );

        // Update store availability based on aggregated status
        this.updateStoreAvailability(store, aggregated);
      });

    } catch (error) {
      console.error('PickupOptions: Failed to fetch store availability:', error);
      // Don't fail the whole request, just leave availability as default
    }
  }

  /**
   * Get variant IDs and quantities for availability check
   * @returns {{ variantIds: string[], quantities: number[] }}
   */
  getVariantsForAvailability() {
    const context = this.data.context;

    if (context === 'cart') {
      // Cart context: use line_items from cart data
      const variantIds = this.data.shippableLineItems || [];
      const quantities = this.data.quantities || variantIds.map(() => 1);
      return { variantIds: variantIds.map(String), quantities };
    } else {
      // PDP context: use single variant
      const variantId = this.data.variantId;
      if (!variantId) {
        return { variantIds: [], quantities: [] };
      }
      return { variantIds: [String(variantId)], quantities: [1] };
    }
  }

  /**
   * Update store object with availability data
   * Consolidated single status matching checkout-ui component behavior:
   * - 'available': All items in stock (success)
   * - 'unavailable': No items in stock (critical)
   * - 'partial': Some items in stock (warning)
   *
   * Also calculates in-store availability based on instore_safety_stock config threshold
   * In-store is only shown on PDP context
   *
   * @param {Object} store - Store object to update
   * @param {Object} aggregated - Aggregated availability from StorefrontService
   */
  updateStoreAvailability(store, aggregated) {
    const { totalCount, details } = aggregated;

    // Single source of truth: use Click & Collect calculation for both lines
    // so Click & Collect and In-store show the same status for this location
    const ccStatus = this.calculateClickCollectAvailability(details);
    const status = ccStatus.status;
    const availableCount = ccStatus.availableCount;
    const unavailableCount = totalCount - availableCount;

    store.availability = {
      status,
      availableCount,
      unavailableCount,
      totalCount,
      statusText: this.getAvailabilityStatusText(status, availableCount, totalCount)
    };

    store.instoreAvailability = {
      status,
      availableCount,
      unavailableCount,
      totalCount,
      statusText: this.getInstoreAvailabilityStatusText(status, availableCount, totalCount)
    };
  }

  /**
   * Calculate Click & Collect availability based on safety stock threshold
   * Available when BOTH conditions are met:
   * 1. quantityAvailable >= quantityRequired (can fulfill the order)
   * 2. quantityAvailable > threshold (inventory above safety stock)
   *
   * @param {Array} details - Availability details from aggregated data
   * @returns {{ status: string, availableCount: number }}
   */
  calculateClickCollectAvailability(details) {
    if (!details || details.length === 0) {
      return { status: 'unavailable', availableCount: 0 };
    }

    // Get threshold from config or use default (10)
    const threshold = this.data?.cc_safety_stock ?? 10;
    let availableCount = 0;

    details.forEach(detail => {
      const quantityRequired = detail.quantityRequired || 1;
      // Use only this location's stock (from GraphQL storeAvailability). No DC stock.
      const quantityAvailable = detail.quantityAvailable ?? 0;
      
      // Available if BOTH conditions are met:
      // 1. Can fulfill the order: inventory >= quantity
      // 2. Inventory above safety stock: inventory > threshold
      const canFulfill = quantityAvailable >= quantityRequired;
      const aboveThreshold = quantityAvailable > threshold;
      
      if (canFulfill && aboveThreshold) {
        availableCount++;
      }
    });

    const totalCount = details.length;

    // Determine status
    let status;
    if (availableCount === totalCount) {
      status = 'available';
    } else if (availableCount > 0) {
      status = 'partial';
    } else {
      status = 'unavailable';
    }

    return { status, availableCount };
  }

  /**
   * Calculate in-store availability based on safety stock threshold
   * Available when BOTH conditions are met:
   * 1. quantityAvailable >= quantityRequired (can fulfill the order)
   * 2. quantityAvailable > threshold (inventory above safety stock)
   *
   * @param {Array} details - Availability details from aggregated data
   * @returns {{ status: string, availableCount: number }}
   */
  calculateInstoreAvailability(details) {
    if (!details || details.length === 0) {
      return { status: 'unavailable', availableCount: 0 };
    }

    // Get threshold from config or use default
    const threshold = this.data?.instore_safety_stock ?? PickupOptions.DEFAULT_INSTORE_SAFETY_STOCK;
    let availableCount = 0;

    details.forEach(detail => {
      const quantityRequired = detail.quantityRequired || 1;
      const quantityAvailable = detail.quantityAvailable || 0;
      
      // Available if BOTH conditions are met:
      // 1. Can fulfill the order: inventory >= quantity
      // 2. Inventory above safety stock: inventory > threshold
      const canFulfill = quantityAvailable >= quantityRequired;
      const aboveThreshold = quantityAvailable > threshold;
      
      if (canFulfill && aboveThreshold) {
        availableCount++;
      }
    });

    const totalCount = details.length;

    // Determine status
    let status;
    if (availableCount === totalCount) {
      status = 'available';
    } else if (availableCount > 0) {
      status = 'partial';
    } else {
      status = 'unavailable';
    }

    return { status, availableCount };
  }

  /**
   * Get status text for in-store availability
   *
   * @param {'available' | 'partial' | 'unavailable'} status
   * @param {number} availableCount
   * @param {number} totalCount
   * @returns {string}
   */
  getInstoreAvailabilityStatusText(status, availableCount, totalCount) {
    const context = {
      availability: {
        all: totalCount,
        available: availableCount,
        unavailable: totalCount - availableCount,
        quantityAvailable: availableCount
      }
    };

    switch (status) {
      case 'available':
        return this.t('delivery.pickup.instore_available', context);

      case 'partial':
        return this.t('delivery.pickup.instore_partially_available', context);

      case 'unavailable':
      default:
        return this.t('delivery.pickup.instore_unavailable', context);
    }
  }

  /**
   * Get status text for store availability
   * Uses store_* translation keys for store cards in popup
   *
   * Context format (matches checkout-ui):
   * {
   *   availability: {
   *     all: number,           // Total items in cart
   *     available: number,     // Items available at this store
   *     unavailable: number,   // Items not available at this store
   *     quantityAvailable: number  // Raw quantity available (for single variant)
   *   }
   * }
   *
   * @param {'available' | 'partial' | 'unavailable'} status
   * @param {number} availableCount
   * @param {number} totalCount
   * @returns {string}
   */
  getAvailabilityStatusText(status, availableCount, totalCount) {
    // Build availability context for translations (wrapped in 'availability' object)
    const context = {
      availability: {
        all: totalCount,
        available: availableCount,
        unavailable: totalCount - availableCount,
        quantityAvailable: availableCount
      }
    };

    // PDP context: Simple "Click and Collect" / "Click and Collect not available"
    if (this.data?.context === 'pdp') {
      switch (status) {
        case 'available':
          return this.t('delivery.pickup.cc_available', context);
        case 'partial':
        case 'unavailable':
        default:
          return this.t('delivery.pickup.cc_unavailable', context);
      }
    }

    // Cart context: Detailed status messages
    switch (status) {
      case 'available':
        return this.t('delivery.pickup.store_available', context);

      case 'partial':
        return this.t('delivery.pickup.store_partially_available', context);

      case 'unavailable':
      default:
        return this.t('delivery.pickup.store_unavailable', context);
    }
  }

  /**
   * Filter locations based on search query
   * Matches against name, city, zip code, province, and address
   * @param {Array} locations - All locations from API
   * @param {string} query - Search query
   * @returns {Array} Filtered locations
   */
  filterLocationsByQuery(locations, query) {
    const normalizedQuery = query.toLowerCase().trim();

    return locations.filter(location => {
      const address = location.address || {};

      // Check if query matches any location field
      const searchableFields = [
        location.name,
        address.city,
        address.zip,
        address.province,
        address.provinceCode,
        address.address1,
        address.formatted?.join(' ')
      ];

      return searchableFields.some(field =>
        field && field.toLowerCase().includes(normalizedQuery)
      );
    });
  }

  /**
   * Transform Storefront API location to store format expected by UI
   * Availability will be enriched later via enrichStoresWithAvailability()
   * @param {Object} location - Location from Storefront API
   * @returns {Object} Store object for rendering
   */
  transformLocationToStore(location) {
    const address = location.address || {};

    return {
      id: location.id,
      name: location.name,
      address: this.formatStoreAddress(address),
      addressFormatted: address.formatted?.join(', ') || '',
      city: address.city,
      province: address.province,
      provinceCode: address.provinceCode,
      zip: address.zip,
      country: address.country,
      countryCode: address.countryCode,
      phone: address.phone,
      coordinates: {
        latitude: address.latitude,
        longitude: address.longitude
      },
      // Distance will be calculated by calculateStoreDistances() using GeocodingService
      distance: null,
      distanceText: null,
      // Click & Collect availability - will be updated by enrichStoresWithAvailability()
      // Initial state is 'loading' until inventory data is fetched
      availability: {
        status: 'loading', // 'loading' | 'available' | 'partial' | 'unavailable'
        availableCount: 0,
        totalCount: 0,
        statusText: this.t('delivery.pickup.checking_stock')
      },
      // In-store availability (PDP only) - will be updated by enrichStoresWithAvailability()
      instoreAvailability: {
        status: 'loading',
        availableCount: 0,
        totalCount: 0,
        statusText: this.t('delivery.pickup.checking_stock')
      },
      disabled: location.localPickup.enabled === false,
      // Raw location data for reference
      _raw: location
    };
  }

  /**
   * Format store address from Storefront API address object
   * @param {Object} address - Address object from API
   * @returns {string} Formatted address string
   */
  formatStoreAddress(address) {
    const parts = [];

    if (address.address1) parts.push(address.address1);
    if (address.address2) parts.push(address.address2);
    if (address.city) parts.push(address.city);
    if (address.provinceCode) parts.push(address.provinceCode);
    if (address.zip) parts.push(address.zip);

    return parts.join(', ');
  }

  // ============================================
  // VIEW MANAGEMENT
  // ============================================

  /**
   * Show specific view
   * Form view is always visible - loading, results, and empty appear below it
   * @param {'form' | 'loading' | 'results' | 'empty'} viewName
   */
  showView(viewName) {
    this.state.view = viewName;

    // Form is always visible - users can always see and modify their search
    // Only toggle loading, results, and empty views
    if (this.loadingView) {
      this.loadingView.classList.toggle('hidden', viewName !== this.VIEW_LOADING);
    }
    if (this.resultsView) {
      this.resultsView.classList.toggle('hidden', viewName !== this.VIEW_RESULTS);
    }
    if (this.emptyView) {
      this.emptyView.classList.toggle('hidden', viewName !== this.VIEW_EMPTY);
    }

    // Hide stock filter when no results (empty view) or loading
    // Filter is only useful when there are stores to filter
    // if (this.stockFilter) {
    //   const showFilter = viewName === this.VIEW_RESULTS;
    //   this.stockFilter.closest('.pickup-options__filter')?.classList.toggle('hidden', !showFilter);
    // }
  }

  /**
   * Clear results and reset to form view
   */
  clearResults() {
    this.state.stores = [];
    this.state.filteredStores = [];
    this.state.selectedStoreId = null;
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = '';
    }
    this.showView(this.VIEW_FORM);
    this.hideFooter();
    this.emitStatusChange('normal');
  }

  // ============================================
  // FOOTER MANAGEMENT
  // ============================================

  /**
   * Show footer with select button
   */
  showFooter() {
    if (this.footerActions) {
      this.footerActions.classList.remove('hidden');
    }
    this.updateSelectButton();
  }

  /**
   * Hide footer
   */
  hideFooter() {
    if (this.footerActions) {
      this.footerActions.classList.add('hidden');
    }
  }

  // ============================================
  // RENDERING
  // ============================================

  /**
   * Render store results
   * @param {Array} stores
   */
  renderStores(stores) {
    if (!this.resultsContainer) return;

    const fragment = document.createDocumentFragment();

    stores.forEach((store, index) => {
      const item = this.getTemplate('store-card');
      if (!item) return;

      const container = item.querySelector('.pickup-options__store-card') || item.firstElementChild;

      if (!container) return;

      // Set store ID
      container.dataset.storeId = String(store.id || index);

      // Check if store is disabled
      const isDisabled = store.disabled === true;
      if (isDisabled) {
        container.classList.add('pickup-options__store-card--disabled');
        container.setAttribute('aria-disabled', 'true');
        container.setAttribute('tabindex', '-1');
      }

      // Check if this store was previously selected
      const isSelected = String(store.id) === String(this.state.selectedStoreId);
      container.setAttribute('aria-checked', String(isSelected));
      if (isSelected) {
        container.classList.add('pickup-options__store-card--selected');
      }

      // Populate fields
      const nameEl = container.querySelector('[data-ref="store-name"]');
      const distanceEl = container.querySelector('[data-ref="store-distance"]');
      const addressEl = container.querySelector('[data-ref="store-address"]');

      if (nameEl) nameEl.textContent = store.name || '';
      if (distanceEl) distanceEl.textContent = this.formatDistance(store.distance);
      if (addressEl) addressEl.textContent = this.formatAddress(store);

      // Populate availability status
      this.renderStoreAvailability(container, store);

      fragment.appendChild(item);
    });

    this.resultsContainer.innerHTML = '';
    this.resultsContainer.appendChild(fragment);

    // Show footer with select button
    this.showFooter();
  }

  /**
   * Render store availability - Click & Collect and In-store status
   * Matches checkout-ui/src/checkout/pickup-location-option-item.render-after.tsx behavior
   *
   * @param {HTMLElement} container - Store card element
   * @param {Object} store - Store object with availability data
   */
  renderStoreAvailability(container, store) {
    // Render Click & Collect status
    const availabilityEl = container.querySelector('[data-ref="store-availability"]');
    if (availabilityEl) {
      const availability = store.availability || {};
      const status = availability.status || 'loading';
      const statusText = availability.statusText || this.t('delivery.pickup.checking_stock');

      // Set data-status attribute for CSS styling
      availabilityEl.dataset.status = status;

      // Update status text
      const textEl = availabilityEl.querySelector('[data-ref="availability-text"]');
      if (textEl) {
        textEl.textContent = statusText;
      }
    }

    // Render In-store status (PDP only)
    this.renderInstoreAvailability(container, store);
  }

  /**
   * Render in-store availability status
   * Only shown on PDP context, hidden on cart context
   *
   * @param {HTMLElement} container - Store card element
   * @param {Object} store - Store object with availability data
   */
  renderInstoreAvailability(container, store) {
    const instoreEl = container.querySelector('[data-ref="instore-availability"]');
    if (!instoreEl) return;

    // Only show in-store status on PDP context
    const context = this.data.context;
    if (context !== 'pdp') {
      instoreEl.classList.add('hidden');
      return;
    }

    // Show in-store element
    instoreEl.classList.remove('hidden');

    const instoreAvailability = store.instoreAvailability || {};
    const status = instoreAvailability.status || 'loading';
    const statusText = instoreAvailability.statusText || this.t('delivery.pickup.checking_stock');

    // Set data-status attribute for CSS styling
    instoreEl.dataset.status = status;

    // Update status text
    const textEl = instoreEl.querySelector('[data-ref="instore-text"]');
    if (textEl) {
      textEl.textContent = statusText;
    }
  }

  /**
   * Format distance for display (always in km)
   * @param {number} distance - Distance in km
   * @returns {string}
   */
  formatDistance(distance) {
    if (distance == null) return '';

    // Always show in km with one decimal place
    return `${distance.toFixed(1)} km`;
  }

  /**
   * Format store address
   * @param {Object} store
   * @returns {string}
   */
  formatAddress(store) {
    if (store.formatted_address) return store.formatted_address;
    if (store.address) return store.address;

    const parts = [
      store.street,
      store.city,
      store.state,
      store.postcode
    ].filter(Boolean);

    return parts.join(', ');
  }


  // ============================================
  // ERROR DISPLAY
  // ============================================

  /**
   * Show form-level error
   * @param {string} message
   */
  showFormError(message) {
    if (!this.formMessages) return;

    const errorEl = this.getTemplate('error-message');
    if (!errorEl) return;

    const msgEl = errorEl.querySelector('[data-ref="error-message"]') ||
                  errorEl.querySelector('.pickup-options__error-text');
    if (msgEl) msgEl.textContent = message;

    this.formMessages.innerHTML = '';
    this.formMessages.appendChild(errorEl);
    this.formMessages.classList.remove('hidden');
  }

  /**
   * Hide form messages
   */
  hideFormMessages() {
    if (this.formMessages) {
      this.formMessages.classList.add('hidden');
      this.formMessages.innerHTML = '';
    }
  }

  /**
   * Determine if a search query should be treated as invalid
   * @param {string} query
   * @param {Array} stores
   * @returns {boolean}
   */
  isInvalidSearchQuery(query, stores, allLocations = []) {
    if (!query) return false;
    if (Array.isArray(stores) && stores.length > 0) return false;

    // Numeric queries are already validated by validatePostcode
    if (/^\d+$/.test(query)) return false;

    const hasGeocodeKey = Boolean(this.geocodingService?.apiKey);

    // Layer 1: Check known location names from store data + zipcode provinces
    const knownNames = this.buildKnownLocationNames(allLocations);
    if (this.matchesKnownLocation(query, knownNames)) return false;

    // Layer 2: If geocoding configured, check precision of geocode result
    if (hasGeocodeKey) {
      if (!this.userLocation) return true;
      return !this.hasValidGeocodePrecision();
    }

    // Layer 3: No geocoding, no known match → invalid
    return true;
  }

  // ============================================
  // PARENT COMMUNICATION
  // ============================================

  /**
   * Emit status change to parent
   * @param {'loading' | 'normal' | 'available' | 'partial' | 'unavailable'} status
   * @param {string} label
   * @param {string|null} error
   * @param {string} statusText - Actual status text to display (e.g., "5 of 10 items available")
   * @param {string} instoreStatus - In-store availability status (PDP only)
   * @param {string} instoreText - In-store availability text (PDP only)
   */
  emitStatusChange(status, label = '', error = null, statusText = '', instoreStatus = '', instoreText = '') {
    this.dispatchEvent(new CustomEvent('status-change', {
      bubbles: true,
      detail: { status, label, error, statusText, instoreStatus, instoreText }
    }));
  }

  // ============================================
  // HELPERS
  // ============================================

  /**
   * Get template by name from Liquid
   * @param {string} name - Template name attribute
   * @returns {DocumentFragment|null}
   */
  getTemplate(name) {
    const template = this.querySelector(`template[name="${name}"]`);
    if (!template) {
      console.warn(`PickupOptions: template[name="${name}"] not found`);
      return null;
    }
    return template.content.cloneNode(true);
  }

  /**
   * Get translation using Translator service
   * @param {string} key - Translation key (relative to 'pickup' namespace)
   * @param {Object} variables - Optional variables for interpolation
   * @returns {string}
   */
  t(key, variables = {}) {
    if (this.translator) {
      return this.translator.translate(key, variables);
    }
    return key;
  }
}

// Define the custom element
if (!customElements.get('pickup-options')) {
  customElements.define('pickup-options', PickupOptions);
}

export { PickupOptions };
